/**
 * Graphite twins of the extended role groups (`semanticLiteral.extended.js`) — dark mode, KAN-304 step 2.
 *
 * Rules: neutral greys, muted status hues, no glow, no purple/indigo wash, no gradients on chrome (a wash
 * resolves to ONE flat colour). Text roles reach 4.5:1 on the surface, icons/dots/chart hues 3:1
 * (`themePalettes.test.js`). Every value is `// chosen` (the mockup did not cover these leftovers).
 */

const tint = (rgb, a) => `rgba(${rgb}, ${a})`;
const GREEN = '74, 222, 155';
const AMBER = '242, 182, 76';
const RED = '248, 113, 113';
const BLUE = '130, 178, 232';
const TEAL = '94, 234, 212';
const INDIGO = '165, 180, 252';
const SKY = '125, 211, 252';
const LIME = '163, 217, 106';
const GREY = '180, 184, 191';

const industryTwin = (key, rgb, hex) => ({
  [`${key}Accent`]: hex,
  [`${key}Soft`]: tint(rgb, 0.14),
  [`${key}Border`]: tint(rgb, 0.3),
  [`${key}Focus`]: tint(rgb, 0.5),
});

export const GRAPHITE_EXTENDED = {
  ink: {
    successVivid: '#30D158',
    successDot: '#30D158',
    successText: '#30D158',
    successInk: '#30D158',
    successDeep: '#30D158',
    blueVivid: '#82B2E8',
    blueBright: '#82B2E8',
    blueLight: '#82B2E8',
    blueMid: '#82B2E8',
    blueDeep: '#82B2E8',
    navyDeep: '#82B2E8',
    blueLink: '#82B2E8',
    indigoBright: '#A5B4FC',
    dangerDeep: '#FF6961',
    dangerDeeper: '#FF6961',
    dangerVivid: '#FF6961',
    roseBright: '#FF6961',
    roseStrong: '#FF6961',
    orangeDeep: '#FF9F0A',
    tealVivid: '#5EEAD4',
    tealBright: '#5EEAD4',
    violetVivid: '#C4B5FD',
    amberBright: '#FF9F0A',
    gray400: '#98989D',
    gray700: '#D2D5DA',
    grayFaint: '#8E8E93',
    slate300: '#5C6067',
    ios: '#F5F5F7',
    iosBlue: '#82B2E8',
    alpha62: 'rgba(245, 245, 247,0.62)',
    alpha56: 'rgba(245, 245, 247,0.56)',
    alpha48: 'rgba(245, 245, 247,0.48)',
  },
  tint: {
    logoPlate: '#ffffff', // chosen: a literal paper plate in both themes
    logoTile: '#ffffff', // chosen: white plate behind a logo in Graphite
    otpCell: '#2C2C2E', // chosen: neutral cell a step above the graphite card, no blue cast
    blue50: tint(BLUE, 0.1),
    blue100: tint(BLUE, 0.18),
    blueFaint: tint(BLUE, 0.08),
    blueSoft: tint(BLUE, 0.1),
    panel: '#242426',
    panelFaint: '#202022',
    indigo50: '#242426',
    indigoPill: tint(INDIGO, 0.14),
    grey50: '#242426',
    grey100: '#242426',
    slate25: '#242426',
    green50: tint(GREEN, 0.08),
    green100: tint(GREEN, 0.14),
    rose50: '#261A1B',
    rose100: tint(RED, 0.18),
    orange50: tint(AMBER, 0.1),
    highlight: tint(AMBER, 0.3),
    glass: 'rgba(30, 32, 36, 0.72)',
    ink04: 'rgba(255,255,255,0.05)',
    ink05: 'rgba(255,255,255,0.06)',
  },
  edge: {
    gray200: '#3A3A3C',
    gray300: '#3A3D43',
    slate400: '#6E6E73',
    input: '#6E6E73',
    card: '#3A3A3C',
    soft: '#38383A',
    steel: '#4A4E55',
    open: '#3A3A3C',
    blue: tint(BLUE, 0.25),
    rose: tint(RED, 0.35),
    navy: '#82B2E8',
    hair06: 'rgba(255, 255, 255, 0.06)',
    hair08: 'rgba(255, 255, 255, 0.08)',
    black05: 'rgba(255,255,255,0.06)',
    black06: 'rgba(255,255,255,0.06)',
    glass: 'rgba(255, 255, 255, 0.08)',
    focusAi: tint(BLUE, 0.5),
  },
  wash: {
    followUp: '#242426',
    hero: '#242426',
    warn: '#1C1B18',
    briefing: '#242426',
    briefingStrong: '#242426',
    tile: '#1C1C1E',
    brandFill: '#2F72C4',
    aiFill: '#2F72C4',
    aiBorder: '#2F72C4',
    aiButton: '#2F72C4',
    authButton: '#2F72C4',
    authPage: '#000000', // chosen: full-page messages sit on the same black as sign-in and the splash
    authGround: '#000000',
    authPanel: '#1C1C1E',
    authPanelEdge: '#3A3A3C',
  },
  chart: {
    drafts: '#8E8E93', // approved dark data set: neutral
    sent: '#40C8E0', // cyan
    paid: '#30D158', // success
    overdue: '#FF9F0A', // warning
    sentAccent: '#40C8E0',
    sentBg: tint('64, 200, 224', 0.14),
    purple: '#4A8DD4', // no purple in Graphite: the insights hue becomes the brand data blue
    purpleBg: tint('74, 141, 212', 0.14),
    pink: '#FF6A3D', // coral (= brand.stop)
    pinkBg: tint('255, 106, 61', 0.14),
  },
  industry: {
    ...industryTwin('pool', TEAL, '#5EEAD4'),
    ...industryTwin('hvac', SKY, '#7DD3FC'),
    ...industryTwin('cleaning', GREEN, '#30D158'),
    ...industryTwin('landscaping', LIME, '#A3D96A'),
    ...industryTwin('pest', AMBER, '#FF9F0A'),
    ...industryTwin('other', GREY, '#AEAEB2'),
    defaultFocus: tint(BLUE, 0.5),
    genericFocus: tint(BLUE, 0.5),
  },
};
